import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Form, Badge, Row, Col, Modal, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { api } from '../services/api';
import { Project } from '../types/models';

// ── Types ──────────────────────────────────────────────────────────

interface PipelineConfig {
  id: string; projectId: string; name: string; repoUrl: string;
  repoName: string; repoType: string; devBranch: string;
  betaBranch: string; prodBranch: string; devUrl: string;
  betaUrl: string; prodUrl: string; devDbId: string;
  betaDbId: string; prodDbId: string;
}

interface DeploymentItem {
  id: string; deploymentId: string; itemType: string; itemId: string;
  itemTitle: string; stage: string; status: string; notes: string;
  addedAt: string; promotedAt?: string;
  // Extended fields for multi-repo support
  repoTarget?: string; // 'frontend' | 'backend' | 'both' | ''
}

interface Deployment {
  id: string; title: string; description: string; projectId: string;
  status: string; targetEnv: string; createdBy: string;
  createdAt: string; updatedAt: string; deployedAt?: string;
  branch: string; items: DeploymentItem[];
}

interface DeploymentLogEntry {
  id: string; action: string; fromStage: string; toStage: string;
  actor: string; details: string; createdAt: string;
}

// ── Style constants ────────────────────────────────────────────────

const STAGE_CONFIG = {
  dev:        { label: 'Development', color: '#3b82f6', bg: '#eff6ff', border: '#93c5fd', icon: '🔧' },
  beta:       { label: 'Beta Testing', color: '#f59e0b', bg: '#fffbeb', border: '#fcd34d', icon: '🧪' },
  production: { label: 'Production',  color: '#10b981', bg: '#ecfdf5', border: '#6ee7b7', icon: '🚀' },
} as const;

const REPO_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  frontend: { bg: '#dbeafe', text: '#1d4ed8', label: 'Frontend' },
  backend:  { bg: '#fce7f3', text: '#be185d', label: 'Backend' },
  both:     { bg: '#e0e7ff', text: '#4338ca', label: 'Full Stack' },
  '':       { bg: '#f3f4f6', text: '#6b7280', label: 'Unassigned' },
};

const STATUS_ICONS: Record<string, string> = {
  pending: '○', included: '◐', deployed: '●', skipped: '—',
  draft: '📝', queued: '📋', deploying: '⚡', failed: '❌', 'rolled-back': '↩️',
};

// ── Component ──────────────────────────────────────────────────────

interface PipelineManagerProps {
  projects: Project[];
  defaultProjectId?: string;
  embedded?: boolean;
}

export const PipelineManager: React.FC<PipelineManagerProps> = ({ projects, defaultProjectId, embedded }) => {
  const [selectedProjectId] = useState<string>(defaultProjectId || '');
  const [configs, setConfigs] = useState<PipelineConfig[]>([]);
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState<string | null>(null);
  const [showLogModal, setShowLogModal] = useState<string | null>(null);
  const [deploymentLog, setDeploymentLog] = useState<DeploymentLogEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);

  const [configForm, setConfigForm] = useState<Partial<PipelineConfig>>({
    repoType: 'backend', devBranch: 'dev', betaBranch: 'beta', prodBranch: 'main',
  });
  const [deployForm, setDeployForm] = useState({ title: '', description: '', targetEnv: 'dev', branch: 'dev' });

  const selectedProject = projects.find((p) => p.id === selectedProjectId);

  // ── Data loading ─────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    if (!selectedProjectId) return;
    try {
      const [cfgs, deps] = await Promise.all([
        api.request(`/projects/${selectedProjectId}/pipeline-configs`),
        api.request(`/projects/${selectedProjectId}/deployments`),
      ]);
      setConfigs(cfgs || []);
      setDeployments(deps || []);
    } catch (err: any) {
      setError(err.message);
    }
  }, [selectedProjectId]);

  useEffect(() => { if (selectedProjectId) loadData(); }, [selectedProjectId, loadData]);

  // ── Handlers ─────────────────────────────────────────────────────

  const handleCreateConfig = async () => {
    await api.request(`/projects/${selectedProjectId}/pipeline-configs`, { method: 'POST', body: JSON.stringify(configForm) });
    setShowConfigModal(false);
    setConfigForm({ repoType: 'backend', devBranch: 'dev', betaBranch: 'beta', prodBranch: 'main' });
    loadData();
  };

  const handleDeleteConfig = async (id: string) => {
    if (!window.confirm('Delete this pipeline config?')) return;
    await api.request(`/pipeline-configs/${id}`, { method: 'DELETE' });
    loadData();
  };

  const handleCreateDeployment = async () => {
    await api.request(`/projects/${selectedProjectId}/deployments`, { method: 'POST', body: JSON.stringify(deployForm) });
    setShowDeployModal(false);
    setDeployForm({ title: '', description: '', targetEnv: 'dev', branch: 'dev' });
    loadData();
  };

  const handlePromote = async (depId: string, toStage: string) => {
    if (!window.confirm(`Promote to ${toStage.toUpperCase()}?`)) return;
    await api.request(`/deployments/${depId}/promote`, { method: 'POST', body: JSON.stringify({ toStage }) });
    loadData();
  };

  const handleDeleteDeployment = async (id: string) => {
    if (!window.confirm('Delete this deployment?')) return;
    await api.request(`/deployments/${id}`, { method: 'DELETE' });
    loadData();
  };

  const handleAddItem = async (depId: string, type: string, id: string, title: string, repoTarget: string) => {
    await api.request(`/deployments/${depId}/items`, {
      method: 'POST',
      body: JSON.stringify({ itemType: type, itemId: id, itemTitle: title, notes: repoTarget }),
    });
    setShowAddItemModal(null);
    loadData();
  };

  const handleRemoveItem = async (depId: string, itemId: string) => {
    await api.request(`/deployments/${depId}/items/${itemId}`, { method: 'DELETE' });
    loadData();
  };

  const handleShowLog = async (depId: string) => {
    const log = await api.request(`/deployments/${depId}/log`);
    setDeploymentLog(log || []);
    setShowLogModal(depId);
  };

  // ── Gather tasks from project ────────────────────────────────────

  const allItems: { id: string; title: string; type: string; parent: string; status: string }[] = [];
  if (selectedProject) {
    for (const goal of selectedProject.goals || []) {
      allItems.push({ id: goal.id, title: goal.title, type: 'goal', parent: '', status: goal.status });
      for (const task of goal.tasks || []) {
        allItems.push({ id: task.id, title: task.title, type: 'task', parent: goal.title, status: task.status });
      }
    }
  }

  // ── Group deployments by stage for Kanban view ───────────────────

  const stages = ['dev', 'beta', 'production'] as const;
  const depsByStage: Record<string, Deployment[]> = { dev: [], beta: [], production: [] };
  for (const dep of deployments) {
    const key = dep.targetEnv in depsByStage ? dep.targetEnv : 'dev';
    depsByStage[key].push(dep);
  }

  // ── Render ───────────────────────────────────────────────────────

  if (!selectedProjectId && !embedded) {
    return (
      <Card className="text-center p-4">
        <p className="text-muted">Select a project to manage its deployment pipeline.</p>
      </Card>
    );
  }

  return (
    <div>
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-2">{error}</Alert>}

      {/* ── Toolbar ─────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <Button size="sm" variant="primary" onClick={() => setShowDeployModal(true)}>
          + New Deployment
        </Button>
        <Button size="sm" variant="outline-secondary" onClick={() => setShowSettings(!showSettings)}>
          ⚙ {showSettings ? 'Hide' : 'Show'} Config
        </Button>
        {configs.length > 0 && (
          <div style={{ display: 'flex', gap: '0.35rem', marginLeft: '0.5rem' }}>
            {configs.map((cfg) => (
              <OverlayTrigger key={cfg.id} placement="top" overlay={
                <Tooltip>{cfg.repoName || cfg.repoUrl}<br/>{cfg.devBranch} → {cfg.betaBranch} → {cfg.prodBranch}</Tooltip>
              }>
                <Badge bg={cfg.repoType === 'frontend' ? 'primary' : cfg.repoType === 'backend' ? 'danger' : 'dark'}
                  style={{ cursor: 'pointer', fontSize: '0.7rem' }}>
                  {cfg.name || cfg.repoType}
                </Badge>
              </OverlayTrigger>
            ))}
          </div>
        )}
      </div>

      {/* ── Settings panel (collapsible) ────────────────────────── */}
      {showSettings && (
        <Card className="mb-3" style={{ borderColor: '#e5e7eb' }}>
          <Card.Body>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h6 className="mb-0">Pipeline Repos</h6>
              <Button size="sm" variant="outline-primary" onClick={() => setShowConfigModal(true)}>+ Add Repo</Button>
            </div>
            {configs.length === 0 ? (
              <p className="text-muted small mb-0">No repos configured. Add your frontend and backend repos to track deployments.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {configs.map((cfg) => {
                  const rc = REPO_COLORS[cfg.repoType] || REPO_COLORS[''];
                  return (
                    <div key={cfg.id} style={{
                      display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0.75rem',
                      borderRadius: 8, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0',
                    }}>
                      <Badge style={{ backgroundColor: rc.bg, color: rc.text }}>{rc.label}</Badge>
                      <div style={{ flex: 1 }}>
                        <strong style={{ fontSize: '0.85rem' }}>{cfg.name}</strong>
                        <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>
                          {cfg.repoName} • {cfg.devBranch} → {cfg.betaBranch} → {cfg.prodBranch}
                        </div>
                        <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                          {cfg.devUrl && <span>Dev: {cfg.devUrl} </span>}
                          {cfg.betaUrl && <span>Beta: {cfg.betaUrl} </span>}
                          {cfg.prodUrl && <span>Prod: {cfg.prodUrl}</span>}
                        </div>
                      </div>
                      <Button variant="link" size="sm" className="text-danger p-0" onClick={() => handleDeleteConfig(cfg.id)}>×</Button>
                    </div>
                  );
                })}
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* ── Kanban Pipeline Board ───────────────────────────────── */}
      <Row>
        {stages.map((stage) => {
          const cfg = STAGE_CONFIG[stage];
          const deps = depsByStage[stage];
          return (
            <Col key={stage} md={4}>
              <div style={{
                backgroundColor: cfg.bg, border: `2px solid ${cfg.border}`,
                borderRadius: 12, padding: '0.75rem', minHeight: 300,
              }}>
                {/* Column header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span style={{ fontSize: '1.1rem' }}>{cfg.icon}</span>
                    <h6 style={{ margin: 0, color: cfg.color, fontWeight: 700 }}>{cfg.label}</h6>
                  </div>
                  <Badge bg="light" text="dark" style={{ fontSize: '0.7rem' }}>{deps.length}</Badge>
                </div>

                {/* Deployment cards */}
                {deps.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '2rem 0.5rem', color: '#94a3b8', fontSize: '0.8rem' }}>
                    No deployments in {cfg.label.toLowerCase()}
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {deps.map((dep) => (
                    <Card key={dep.id} style={{
                      borderRadius: 8, border: '1px solid #e2e8f0',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    }}>
                      <Card.Body style={{ padding: '0.65rem' }}>
                        {/* Deployment header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.4rem' }}>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{dep.title}</div>
                            {dep.description && <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>{dep.description}</div>}
                          </div>
                          <span style={{ fontSize: '0.75rem' }}>{STATUS_ICONS[dep.status] || '?'}</span>
                        </div>

                        {/* Items */}
                        {dep.items && dep.items.length > 0 && (
                          <div style={{ marginBottom: '0.4rem' }}>
                            {dep.items.map((item) => {
                              const repoTarget = item.notes || '';
                              const rc = REPO_COLORS[repoTarget] || REPO_COLORS[''];
                              return (
                                <div key={item.id} style={{
                                  display: 'flex', alignItems: 'center', gap: '0.35rem',
                                  padding: '0.2rem 0.4rem', borderRadius: 4,
                                  backgroundColor: '#f8fafc', marginBottom: '0.2rem',
                                  fontSize: '0.75rem',
                                }}>
                                  <span style={{ fontSize: '0.7rem' }}>{STATUS_ICONS[item.status] || '○'}</span>
                                  {repoTarget && (
                                    <Badge style={{ backgroundColor: rc.bg, color: rc.text, fontSize: '0.6rem', padding: '0.1rem 0.3rem' }}>
                                      {rc.label}
                                    </Badge>
                                  )}
                                  <span style={{ flex: 1 }}>{item.itemTitle}</span>
                                  {dep.status !== 'deployed' && (
                                    <button onClick={() => handleRemoveItem(dep.id, item.id)}
                                      style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '0.7rem', padding: 0 }}>×</button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Action buttons */}
                        <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                          <Button size="sm" variant="outline-secondary" style={{ fontSize: '0.68rem', padding: '0.15rem 0.4rem' }}
                            onClick={() => setShowAddItemModal(dep.id)}>+ Item</Button>
                          {stage === 'dev' && (
                            <Button size="sm" variant="warning" style={{ fontSize: '0.68rem', padding: '0.15rem 0.4rem' }}
                              onClick={() => handlePromote(dep.id, 'beta')}>→ Beta</Button>
                          )}
                          {stage === 'beta' && (
                            <Button size="sm" variant="success" style={{ fontSize: '0.68rem', padding: '0.15rem 0.4rem' }}
                              onClick={() => handlePromote(dep.id, 'production')}>→ Prod</Button>
                          )}
                          <Button size="sm" variant="link" style={{ fontSize: '0.68rem', padding: '0.15rem 0.3rem', color: '#6b7280' }}
                            onClick={() => handleShowLog(dep.id)}>📋</Button>
                          <Button size="sm" variant="link" style={{ fontSize: '0.68rem', padding: '0.15rem 0.3rem', color: '#dc2626' }}
                            onClick={() => handleDeleteDeployment(dep.id)}>🗑</Button>
                        </div>

                        {/* Metadata */}
                        <div style={{ fontSize: '0.65rem', color: '#94a3b8', marginTop: '0.3rem' }}>
                          {dep.branch && <span>branch: {dep.branch} • </span>}
                          {new Date(dep.createdAt).toLocaleDateString()}
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              </div>
            </Col>
          );
        })}
      </Row>

      {/* ── Add Pipeline Config Modal ───────────────────────────── */}
      <Modal show={showConfigModal} onHide={() => setShowConfigModal(false)} size="lg">
        <Modal.Header closeButton><Modal.Title>Add Pipeline Repo</Modal.Title></Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small fw-bold">Name</Form.Label>
                <Form.Control size="sm" value={configForm.name || ''} onChange={(e) => setConfigForm({ ...configForm, name: e.target.value })} placeholder="e.g. Backend API" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small fw-bold">Type</Form.Label>
                <Form.Select size="sm" value={configForm.repoType} onChange={(e) => setConfigForm({ ...configForm, repoType: e.target.value })}>
                  <option value="backend">Backend</option>
                  <option value="frontend">Frontend</option>
                  <option value="monorepo">Monorepo</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}><Form.Group className="mb-2"><Form.Label className="small fw-bold">Repo URL</Form.Label><Form.Control size="sm" value={configForm.repoUrl || ''} onChange={(e) => setConfigForm({ ...configForm, repoUrl: e.target.value })} placeholder="git@github.com:..." /></Form.Group></Col>
            <Col md={6}><Form.Group className="mb-2"><Form.Label className="small fw-bold">Repo Name</Form.Label><Form.Control size="sm" value={configForm.repoName || ''} onChange={(e) => setConfigForm({ ...configForm, repoName: e.target.value })} placeholder="owner/repo" /></Form.Group></Col>
          </Row>
          <Row>
            <Col md={4}><Form.Group className="mb-2"><Form.Label className="small fw-bold">Dev Branch</Form.Label><Form.Control size="sm" value={configForm.devBranch || 'dev'} onChange={(e) => setConfigForm({ ...configForm, devBranch: e.target.value })} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-2"><Form.Label className="small fw-bold">Beta Branch</Form.Label><Form.Control size="sm" value={configForm.betaBranch || 'beta'} onChange={(e) => setConfigForm({ ...configForm, betaBranch: e.target.value })} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-2"><Form.Label className="small fw-bold">Prod Branch</Form.Label><Form.Control size="sm" value={configForm.prodBranch || 'main'} onChange={(e) => setConfigForm({ ...configForm, prodBranch: e.target.value })} /></Form.Group></Col>
          </Row>
          <Row>
            <Col md={4}><Form.Group className="mb-2"><Form.Label className="small fw-bold">Dev URL</Form.Label><Form.Control size="sm" value={configForm.devUrl || ''} onChange={(e) => setConfigForm({ ...configForm, devUrl: e.target.value })} placeholder="https://dev-..." /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-2"><Form.Label className="small fw-bold">Beta URL</Form.Label><Form.Control size="sm" value={configForm.betaUrl || ''} onChange={(e) => setConfigForm({ ...configForm, betaUrl: e.target.value })} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-2"><Form.Label className="small fw-bold">Prod URL</Form.Label><Form.Control size="sm" value={configForm.prodUrl || ''} onChange={(e) => setConfigForm({ ...configForm, prodUrl: e.target.value })} /></Form.Group></Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowConfigModal(false)}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleCreateConfig}>Save Repo Config</Button>
        </Modal.Footer>
      </Modal>

      {/* ── New Deployment Modal ─────────────────────────────────── */}
      <Modal show={showDeployModal} onHide={() => setShowDeployModal(false)}>
        <Modal.Header closeButton><Modal.Title>New Deployment</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2"><Form.Label className="small fw-bold">Title</Form.Label>
            <Form.Control size="sm" value={deployForm.title} onChange={(e) => setDeployForm({ ...deployForm, title: e.target.value })} placeholder="e.g. Sprint 12 Release" /></Form.Group>
          <Form.Group className="mb-2"><Form.Label className="small fw-bold">Description</Form.Label>
            <Form.Control size="sm" as="textarea" rows={2} value={deployForm.description} onChange={(e) => setDeployForm({ ...deployForm, description: e.target.value })} /></Form.Group>
          <Row>
            <Col>
              <Form.Group className="mb-2"><Form.Label className="small fw-bold">Target</Form.Label>
                <Form.Select size="sm" value={deployForm.targetEnv} onChange={(e) => setDeployForm({ ...deployForm, targetEnv: e.target.value })}>
                  <option value="dev">Development</option><option value="beta">Beta</option><option value="production">Production</option>
                </Form.Select></Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-2"><Form.Label className="small fw-bold">Branch</Form.Label>
                <Form.Control size="sm" value={deployForm.branch} onChange={(e) => setDeployForm({ ...deployForm, branch: e.target.value })} /></Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowDeployModal(false)}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleCreateDeployment} disabled={!deployForm.title}>Create</Button>
        </Modal.Footer>
      </Modal>

      {/* ── Add Item Modal (with repo target) ───────────────────── */}
      <Modal show={!!showAddItemModal} onHide={() => setShowAddItemModal(null)} size="lg">
        <Modal.Header closeButton><Modal.Title>Add Items to Deployment</Modal.Title></Modal.Header>
        <Modal.Body>
          {allItems.length === 0 ? (
            <p className="text-muted">No tasks or goals in this project.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
              {allItems.map((item) => (
                <div key={item.id} style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.6rem',
                  borderRadius: 6, border: '1px solid #e2e8f0', fontSize: '0.85rem',
                }}>
                  <Badge bg={item.type === 'goal' ? 'info' : 'secondary'} style={{ fontSize: '0.65rem' }}>{item.type}</Badge>
                  <span style={{ flex: 1 }}>
                    {item.title}
                    {item.parent && <small className="text-muted ms-1">({item.parent})</small>}
                  </span>
                  <Badge bg="light" text="dark" style={{ fontSize: '0.65rem' }}>{item.status}</Badge>
                  {/* Repo target buttons */}
                  <div style={{ display: 'flex', gap: '0.2rem' }}>
                    {configs.length > 0 ? (
                      <>
                        {configs.filter(c => c.repoType === 'frontend').length > 0 && (
                          <Button size="sm" variant="outline-primary" style={{ fontSize: '0.65rem', padding: '0.1rem 0.35rem' }}
                            onClick={() => handleAddItem(showAddItemModal!, item.type, item.id, item.title, 'frontend')}>
                            + FE
                          </Button>
                        )}
                        {configs.filter(c => c.repoType === 'backend').length > 0 && (
                          <Button size="sm" variant="outline-danger" style={{ fontSize: '0.65rem', padding: '0.1rem 0.35rem' }}
                            onClick={() => handleAddItem(showAddItemModal!, item.type, item.id, item.title, 'backend')}>
                            + BE
                          </Button>
                        )}
                        <Button size="sm" variant="outline-dark" style={{ fontSize: '0.65rem', padding: '0.1rem 0.35rem' }}
                          onClick={() => handleAddItem(showAddItemModal!, item.type, item.id, item.title, 'both')}>
                          + Both
                        </Button>
                      </>
                    ) : (
                      <Button size="sm" variant="outline-primary" style={{ fontSize: '0.65rem', padding: '0.1rem 0.35rem' }}
                        onClick={() => handleAddItem(showAddItemModal!, item.type, item.id, item.title, '')}>
                        + Add
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* ── Deployment Log Modal ─────────────────────────────────── */}
      <Modal show={!!showLogModal} onHide={() => setShowLogModal(null)}>
        <Modal.Header closeButton><Modal.Title>Deployment Log</Modal.Title></Modal.Header>
        <Modal.Body>
          {deploymentLog.length === 0 ? (
            <p className="text-muted small">No log entries.</p>
          ) : (
            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              {deploymentLog.map((entry) => (
                <div key={entry.id} style={{ padding: '0.3rem 0', borderBottom: '1px solid #f3f4f6', fontSize: '0.8rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <strong>{entry.action}</strong>
                    <small className="text-muted">{new Date(entry.createdAt).toLocaleString()}</small>
                  </div>
                  {entry.fromStage && entry.toStage && (
                    <span className="text-muted small">{entry.fromStage} → {entry.toStage}</span>
                  )}
                  {entry.details && <div className="text-muted small">{entry.details}</div>}
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};
